import { rentalscapeStats } from '@/data/data-selectors';
import * as userActions from '@/user/user-actions';
import {
  authorizedPlacesRsuite,
  currentCity as currentCitySelector,
} from '@/user/user-selectors';
import { toaster } from '@/utils/toaster';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Modal } from 'rsuite';
import { DeckardCognitoUser } from '../deckard-cognito-user';
import { LoadingPicker } from '../user-profile/modules/loading-picker';
import {
  Avatar,
  ButtonContainer,
  Container,
  FixedWidthButton,
} from '../user-profile/user-profile-styles';

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function UserPlaceSwitcher({ open, setOpen }: Props) {
  const dispatch = useDispatch();

  const history = useHistory();

  const rentalscapeStatsObj = useSelector(rentalscapeStats);
  const currentCity = useSelector(currentCitySelector);

  const userAuthorizedPlacesRsuite = useSelector(authorizedPlacesRsuite);

  const [selected, setSelected] = useState(currentCity);

  const onDefaultCityButtonClick = useCallback(async () => {
    try {
      await DeckardCognitoUser.setDefaultCity(selected);
      toaster.success(`Default city changed to: ${selected}`);
    } catch (err) {
      toaster.error(`Unable to switch to: ${selected}. Please try again.`);
    }
  }, [selected]);

  const changePlace = useCallback(() => {
    setOpen(false);
    history.push(`/${selected}/home`);
    dispatch(userActions.setCurrentCityEverywhere({ city: selected }));
  }, [dispatch, history, selected, setOpen]);

  if (!open) return null;

  return (
    <Modal
      data-testid="user-place-switcher"
      overflow={false}
      size="xs"
      open={open}
      onClose={() => setOpen(false)}
    >
      <Modal.Header></Modal.Header>
      <Modal.Body>
        <Avatar src={rentalscapeStatsObj?.logo} />

        <Container>
          <p>Current City</p>

          <LoadingPicker
            placeholder="Select City/County"
            value={selected}
            onSelect={setSelected}
            cleanable={false}
            data={userAuthorizedPlacesRsuite}
          />

          <ButtonContainer>
            <FixedWidthButton
              data-testid="set-default-city-button"
              onClick={onDefaultCityButtonClick}
            >
              Set as Default City
            </FixedWidthButton>

            <FixedWidthButton
              data-testid="set-current-city-button"
              onClick={changePlace}
            >
              Change City
            </FixedWidthButton>
          </ButtonContainer>
        </Container>
      </Modal.Body>
    </Modal>
  );
}
